import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import PasswordInput from '@/components/PasswordInput';
import { toast } from 'sonner';
import { Shield, Lock, User, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const { adminLogin, user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  if (user) { navigate('/dashboard'); return null; }

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminLogin(username, password);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Credenciales de admin invalidas');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success(t('temp_password_sent'));
      setShowForgot(false);
      setForgotEmail('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="login-bg flex items-center justify-center min-h-screen" data-testid="admin-login-page">
      <div className="glass-card p-8 sm:p-10 w-full max-w-md mx-4 animate-slide-up z-10">
        <div className="text-center mb-8">
          <Shield className="w-16 h-16 text-blue-400 mx-auto mb-4" />
          <h1 className="text-3xl font-black text-white neon-glow" style={{ fontFamily: 'Montserrat' }}>
            Active<span className="text-red-400">English</span>
          </h1>
          <p className="text-slate-400 mt-2 text-sm">{t('admin_login')}</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-slate-300">{t('username')}</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="admin-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={t('username')}
                className="pl-10 bg-white/10 border-white/15 text-white placeholder:text-slate-500 h-12 rounded-xl"
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">{t('password')}</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
              <PasswordInput
                data-testid="admin-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('password')}
                className="pl-10 pr-24 bg-white/10 border-white/15 text-white placeholder:text-slate-500 h-12 rounded-xl"
                required
              />
            </div>
          </div>
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full h-12 rounded-xl font-bold"
            style={{ fontFamily: 'Montserrat', background: '#2563EB' }}
          >
            {loading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('login_button')}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => setShowForgot(true)}
            className="text-blue-400 text-sm hover:text-blue-300 hover:underline"
          >
            {t('forgot_password')}
          </button>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-slate-400 text-sm hover:text-slate-300 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> {t('back')}
          </Link>
        </div>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('forgot_password')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder={t('email')}
              />
            </div>
            <Button onClick={handleForgotPassword} disabled={forgotLoading} className="w-full">
              {forgotLoading ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : t('send')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
